// MYAC-1522 (onboarding pass) — the one true first step of a person with nothing
// to look at, with what it needs to be said: the company's name, the projects an
// admin is outside of, the admins a member can turn to. ONE decision for every
// surface that says it: the empty dashboard and lists (EmptyProjectsState) and
// the welcome letter, so the letter never promises what the page does not offer.
import { computed, onMounted, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Project } from '@/models/Project'
import type { ProjectCreationRequestType } from '@/models/Billing'
import { useFeaturesStore } from '@/stores/features'
import { useMembersStore } from '@/stores/members'
import { useProjectsStore } from '@/stores/projects'
import { useTenantsStore } from '@/stores/tenants'
import { useUsersStore } from '@/stores/users'
import { firstStep, type FirstStep } from '@/utils/firstStep'
import { projectTypeTagKey } from '@/utils/projectTypeLabel'

export interface FirstStepState {
  step: ComputedRef<FirstStep>
  /** False while an admin's read of the company's projects is under way: say nothing yet. */
  ready: ComputedRef<boolean>
  /** That read failed: say so, never offer a create that may duplicate what exists. */
  readFailed: Ref<boolean>
  companyName: ComputedRef<string>
  /** « « Acme Corp » (Veille réglementaire) », the projects this admin is outside of. */
  outsideNames: ComputedRef<string>
  outsideCount: ComputedRef<number>
  /** The company's active admins, by name, for a member to turn to. */
  adminNames: ComputedRef<string>
}

export function useFirstStep(
  scope: Ref<ProjectCreationRequestType | null> | (() => ProjectCreationRequestType | null),
  /** `enabled`: read the company's projects only when the step is going to be said (the letter's « nothing to look at »). */
  opts: { enabled?: () => boolean } = {},
): FirstStepState {
  const enabled = opts.enabled ?? (() => true)
  const scopeOf = typeof scope === 'function' ? scope : () => scope.value
  const { t } = useI18n()
  const featuresStore = useFeaturesStore()
  const membersStore = useMembersStore()
  const tenantsStore = useTenantsStore()
  const projectsStore = useProjectsStore()
  const usersStore = useUsersStore()

  const tenantId = computed(() => tenantsStore.currentTenant?.id ?? '')
  const companyName = computed(() => tenantsStore.currentTenant?.name ?? '')

  // An admin may read every project of the company (firestore.rules, projects read);
  // the ones they are not a member of are invisible to them everywhere else. Read
  // before choosing, so an admin outside existing projects is never offered a duplicate.
  const companyProjects = ref<Project[] | null>(null)
  const readFailed = ref(false)
  const outside = computed(() => (companyProjects.value ?? []).filter((p) =>
    !(p.members ?? []).includes(usersStore.user?.uid ?? '') && (!scopeOf() || p.type === scopeOf())))
  const outsideNames = computed(() => outside.value
    .map((p) => `« ${p.name ?? ''} » (${t(projectTypeTagKey(p.type))})`)
    .join(', '))
  const ready = computed(() => !enabled() || !featuresStore.isTenantAdmin || !tenantId.value
    || companyProjects.value !== null || readFailed.value)

  const step = computed(() => firstStep({
    isAdmin: featuresStore.isTenantAdmin,
    canCreate: (type) => featuresStore.canCreateProjectOfType(type),
    scope: scopeOf(),
    outsideProjects: outside.value.length,
  }))

  function loadMembers(): void {
    if (step.value.kind === 'ask_admin' && tenantId.value) void membersStore.listTenantMembers(tenantId.value)
  }
  async function loadCompanyProjects(): Promise<void> {
    if (!enabled() || !featuresStore.isTenantAdmin || !tenantId.value || companyProjects.value !== null) return
    const projects = await projectsStore.readProjectsByTenant(tenantId.value)
    readFailed.value = projects === null
    companyProjects.value = projects
  }
  onMounted(() => { loadMembers(); void loadCompanyProjects() })
  watch([tenantId, () => step.value.kind], loadMembers)
  watch([tenantId, () => featuresStore.isTenantAdmin], () => { companyProjects.value = null; void loadCompanyProjects() })
  watch(enabled, (on) => { if (on) void loadCompanyProjects() })

  const adminNames = computed(() => membersStore.tenantMembers
    .filter((m) => m.role === 'admin' && m.active !== false)
    .map((m) => m.display_name?.trim() || m.email || '')
    .filter(Boolean)
    .join(', '))

  return { step, ready, readFailed, companyName, outsideNames, outsideCount: computed(() => outside.value.length), adminNames }
}
